import { NOTE_DEFAULT_SIZE } from "@cohesive/ui-components";
import type { Node } from "@xyflow/react";
import { LATTICE, snapToGrid } from "../canvas-controls.config";

export const RETRO_BOARD_PRESET_ID = "retro-board";

const RETRO_COLUMNS = [
  { title: "Kudos", noteColor: "var(--sticky-butter)" },
  { title: "Keep doing", noteColor: "var(--sticky-meadow)" },
  { title: "Stop doing", noteColor: "var(--sticky-rose)" },
  { title: "Start doing", noteColor: "var(--sticky-mist)" },
] as const;

const NOTE_GAP = 2 * LATTICE;

interface StoredRetroCard {
  columnIndex?: unknown;
  createdAt?: unknown;
  content?: unknown;
  authorName?: unknown;
  votes?: unknown;
}

function cardLine(card: StoredRetroCard, content: string): string {
  const votes = Array.isArray(card.votes) ? card.votes.length : 0;
  const voteText = `${votes} ${votes === 1 ? "vote" : "votes"}`;
  const author =
    typeof card.authorName === "string" ? card.authorName.trim() : "";
  const meta = author ? `${author}, ${voteText}` : voteText;
  return `- ${content.replace(/\n/g, "\n  ")} (${meta})`;
}

/**
 * Sticky notes replacing a retired retro board: one per column holding at
 * least one non-empty card, left to right from the board's position. Ids
 * derive from the board so concurrent editors converting the same board
 * write the same notes instead of duplicates.
 */
export function retroBoardToNotes(board: Node, createdAt: string): Node[] {
  const cards = (board.data as { cards?: unknown }).cards;
  if (!Array.isArray(cards)) return [];

  const x = snapToGrid(board.position.x, LATTICE);
  const y = snapToGrid(board.position.y, LATTICE);
  const notes: Node[] = [];

  RETRO_COLUMNS.forEach((column, columnIndex) => {
    const lines = (cards as StoredRetroCard[]).flatMap((card) => {
      if (card?.columnIndex !== columnIndex) return [];
      const content =
        typeof card.content === "string" ? card.content.trim() : "";
      return content ? [cardLine(card, content)] : [];
    });
    if (lines.length === 0) return;

    notes.push({
      id: `${board.id}-retro-${columnIndex}`,
      type: "textNote",
      position: { x: x + notes.length * (NOTE_DEFAULT_SIZE + NOTE_GAP), y },
      ...(board.parentId ? { parentId: board.parentId } : {}),
      width: NOTE_DEFAULT_SIZE,
      height: NOTE_DEFAULT_SIZE,
      data: {
        title: column.title,
        content: lines.join("\n"),
        note_color: column.noteColor,
        created_at: createdAt,
      },
    });
  });

  return notes;
}

/**
 * A creation time fixed by the board, so the notes rendered before the
 * migration and the ones it writes are identical.
 */
export function retroNotesCreatedAt(board: Node): string {
  const { updated_at, cards } = board.data as {
    updated_at?: unknown;
    cards?: unknown;
  };
  if (typeof updated_at === "string" && !Number.isNaN(Date.parse(updated_at)))
    return new Date(updated_at).toISOString();
  const times = (Array.isArray(cards) ? (cards as StoredRetroCard[]) : [])
    .map((c) => c?.createdAt)
    .filter((t): t is number => typeof t === "number" && Number.isFinite(t));
  return new Date(times.length > 0 ? Math.max(...times) : 0).toISOString();
}
